/**
 * Support Message Service
 *
 * Handles support conversations in the messaging system.
 * Support conversations are group chats with all super admins as participants.
 * Super admins respond as themselves (not anonymously).
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { getLogger } from '@kit/shared/logger';
import { Result } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import {
  batchGetProfilePhotoSignedUrls,
  collectParticipantProfilePhotoPaths,
  enrichParticipantsWithSignedUrls,
  getProfilePhotoSignedUrl,
} from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import { isBalleeUser } from '../../lib/ballee-constants';
import type { ConversationWithDetails } from '../../types';

export class SupportMessageService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Check if a conversation is a support conversation.
   * Checks the metadata.is_support flag.
   */
  async isSupportConversation(
    conversationId: string,
  ): Promise<Result<boolean>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data: conv, error: convError } = await this.client
      .from('conversations')
      .select('metadata')
      .eq('id', conversationId)
      .single();

    if (convError) {
      return Result.fail(this.handleDatabaseError(convError));
    }

    const metadata = conv?.metadata as Record<string, unknown> | null;
    return Result.ok(metadata?.is_support === true);
  }

  /**
   * Get all support conversations.
   * For admin inbox display. Must be called with admin client for RLS bypass.
   */
  async getSupportConversations(): Promise<Result<ConversationWithDetails[]>> {
    const logger = await getLogger();

    // Query conversations with metadata.is_support = true
    const { data, error } = await fetchAllRows((from, to) =>
      this.client
        .from('conversations')
        .select(
          `
        *,
        participants:conversation_participants(
          *,
          profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url)
        )
      `,
        )
        .eq('metadata->>is_support', 'true')
        .is('deleted_at', null)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .order('id')
        .range(from, to),
    );

    if (error) {
      logger.error(
        {
          name: 'support.getConversations.error',
          error: error.message,
        },
        'Failed to fetch conversations',
      );
      return Result.fail(this.handleDatabaseError(error));
    }

    // Collect all profile photo paths from participants
    const allProfilePhotoPaths = (data || []).flatMap((conv) =>
      collectParticipantProfilePhotoPaths(conv.participants || []),
    );

    // Generate signed URLs in batch
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      allProfilePhotoPaths,
    );

    // Transform to ConversationWithDetails with signed URLs
    const conversations = (data || []).map(
      (conv) =>
        ({
          ...conv,
          participants: enrichParticipantsWithSignedUrls(
            conv.participants || [],
            signedUrlMap,
          ),
          my_participation: null, // Super admins see all - set on client side
        }) as ConversationWithDetails,
    );

    return Result.ok(conversations);
  }

  /**
   * Get unread count for support conversations.
   * Aggregates unread count from the current user's participation.
   */
  async getSupportUnreadCount(userId: string): Promise<Result<number>> {
    const uuidValidation = this.validateUUID(userId, 'userId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    // Get all support conversations where this user is a participant
    const { data: supportConvs, error: convError } = await fetchAllRows(
      (from, to) =>
        this.client
          .from('conversations')
          .select('id')
          .eq('metadata->>is_support', 'true')
          .is('deleted_at', null)
          .order('id')
          .range(from, to),
    );

    if (convError) {
      return Result.fail(this.handleDatabaseError(convError));
    }

    if (!supportConvs || supportConvs.length === 0) {
      return Result.ok(0);
    }

    const conversationIds = supportConvs.map((c) => c.id);

    // Get unread counts for this user in those conversations
    const { data, error } = await fetchAllRows((from, to) =>
      this.client
        .from('conversation_participants')
        .select('unread_count')
        .in('conversation_id', conversationIds)
        .eq('user_id', userId)
        .eq('is_active', true)
        .gt('unread_count', 0)
        .order('id')
        .range(from, to),
    );

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const total = (data || []).reduce(
      (sum, p) => sum + (p.unread_count || 0),
      0,
    );
    return Result.ok(total);
  }

  /**
   * Mark a support conversation as read for a user.
   * Uses admin client to update the user's participant record.
   */
  async markAsRead(
    conversationId: string,
    userId: string,
  ): Promise<Result<void>> {
    const logger = await getLogger();
    const convValidation = this.validateUUID(conversationId, 'conversationId');
    if (convValidation.isFailure) {
      return Result.fail(convValidation.getError());
    }

    const userValidation = this.validateUUID(userId, 'userId');
    if (userValidation.isFailure) {
      return Result.fail(userValidation.getError());
    }

    const { error } = await this.client
      .from('conversation_participants')
      .update({
        last_read_at: new Date().toISOString(),
        unread_count: 0,
      })
      .eq('conversation_id', conversationId)
      .eq('user_id', userId);

    if (error) {
      logger.error(
        {
          name: 'support.markAsRead.error',
          conversationId,
          userId,
          error: error.message,
        },
        'Failed to mark support conversation as read',
      );
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Get the dancer (non-admin) participant in a support conversation.
   * The dancer is identified as the conversation creator.
   */
  async getOtherParticipant(conversationId: string): Promise<
    Result<{
      userId: string;
      firstName: string | null;
      lastName: string | null;
      profilePhotoUrl: string | null;
    } | null>
  > {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    // Get the conversation to find the creator
    const { data: conv, error: convError } = await this.client
      .from('conversations')
      .select('created_by')
      .eq('id', conversationId)
      .single();

    if (convError) {
      return Result.fail(this.handleDatabaseError(convError));
    }

    if (!conv?.created_by) {
      return Result.ok(null);
    }

    // Get the creator's profile
    const { data: participant, error: partError } = await this.client
      .from('conversation_participants')
      .select(
        `
        user_id,
        profile:profiles!conversation_participants_user_id_profiles_fkey(
          first_name,
          last_name,
          profile_photo_url
        )
      `,
      )
      .eq('conversation_id', conversationId)
      .eq('user_id', conv.created_by)
      .eq('is_active', true)
      .single();

    if (partError) {
      if (partError.code === 'PGRST116') {
        return Result.ok(null);
      }
      return Result.fail(this.handleDatabaseError(partError));
    }

    // Profile is returned as an object from the FK join
    const profile = participant.profile as {
      first_name: string | null;
      last_name: string | null;
      profile_photo_url: string | null;
    } | null;

    // Generate signed URL for profile photo (storage path → signed URL)
    const profilePhotoUrl = profile?.profile_photo_url
      ? await getProfilePhotoSignedUrl(this.client, profile.profile_photo_url)
      : null;

    return Result.ok({
      userId: participant.user_id,
      firstName: profile?.first_name || null,
      lastName: profile?.last_name || null,
      profilePhotoUrl,
    });
  }

  /**
   * Check if the current authenticated user is a super admin.
   * Uses the is_super_admin RPC function (which checks auth.uid() internally).
   */
  async isCurrentUserSuperAdmin(): Promise<Result<boolean>> {
    const { data, error } = await this.client.rpc('is_super_admin');

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(!!data);
  }

  /**
   * Get super admin user IDs for filtering participants.
   */
  async getSuperAdminUserIds(): Promise<Result<string[]>> {
    const { data, error } = await this.client.rpc('get_super_admin_user_ids');

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok((data as string[]) || []);
  }

  /**
   * Find the dancer in a list of participants (the non-admin, non-system user).
   * Used for displaying who initiated the support request.
   */
  findDancerInParticipants(
    participants: Array<{ user_id: string }>,
    superAdminIds: Set<string>,
  ): { user_id: string } | undefined {
    return participants.find(
      (p) => !superAdminIds.has(p.user_id) && !isBalleeUser(p.user_id),
    );
  }

  // ===========================================================================
  // Lightweight case tracking (status / assignee / labels / internal notes)
  // ===========================================================================

  /**
   * Update the case fields on a support conversation. Only the provided fields
   * are written. Must be called with an admin client (RLS bypass).
   */
  async updateSupportCase(
    conversationId: string,
    fields: {
      status?: SupportCaseStatus;
      assigneeId?: string | null;
      labels?: string[];
    },
  ): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const update: {
      support_status?: SupportCaseStatus;
      support_assignee_id?: string | null;
      support_labels?: string[];
    } = {};
    if (fields.status !== undefined) update.support_status = fields.status;
    if (fields.assigneeId !== undefined) {
      update.support_assignee_id = fields.assigneeId;
    }
    if (fields.labels !== undefined) update.support_labels = fields.labels;

    if (Object.keys(update).length === 0) {
      return Result.ok(undefined);
    }

    const { error } = await this.client
      .from('conversations')
      .update(update)
      .eq('id', conversationId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }
    return Result.ok(undefined);
  }

  /**
   * Add a super-admin internal note (never visible to the requesting user).
   */
  async addInternalNote(
    conversationId: string,
    authorId: string,
    body: string,
    mentionedUserIds: string[],
  ): Promise<Result<{ id: string }>> {
    const convValidation = this.validateUUID(conversationId, 'conversationId');
    if (convValidation.isFailure) {
      return Result.fail(convValidation.getError());
    }

    const { data, error } = await this.client
      .from('support_internal_notes')
      .insert({
        conversation_id: conversationId,
        author_id: authorId,
        body: body.trim(),
        mentioned_user_ids: mentionedUserIds,
      })
      .select('id')
      .single();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }
    return Result.ok({ id: data.id });
  }

  /**
   * List internal notes for a support conversation (super-admin only).
   */
  async getInternalNotes(
    conversationId: string,
  ): Promise<Result<SupportInternalNote[]>> {
    const convValidation = this.validateUUID(conversationId, 'conversationId');
    if (convValidation.isFailure) {
      return Result.fail(convValidation.getError());
    }

    const { data, error } = await fetchAllRows((from, to) =>
      this.client
        .from('support_internal_notes')
        .select(
          'id, conversation_id, author_id, body, mentioned_user_ids, created_at',
        )
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })
        .order('id')
        .range(from, to),
    );

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }
    return Result.ok((data ?? []) as SupportInternalNote[]);
  }

  /**
   * Super admins available for assignment / @mention, with display names.
   */
  async listSupportAdmins(): Promise<Result<SupportAdmin[]>> {
    const idsResult = await this.getSuperAdminUserIds();
    if (idsResult.isFailure) {
      return Result.fail(idsResult.getError());
    }
    const ids = idsResult.getValue();
    if (ids.length === 0) {
      return Result.ok([]);
    }

    // eslint-disable-next-line supabase-query/no-unbounded-select -- primary-key lookup: at most one row per id, and the .in() id list is capped by URL length far below 1000 rows
    const { data, error } = await this.client
      .from('profiles')
      .select('id, first_name, last_name')
      .in('id', ids);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }
    return Result.ok((data ?? []) as SupportAdmin[]);
  }
}

export type SupportCaseStatus = 'ai' | 'open' | 'investigating' | 'resolved';

export interface SupportInternalNote {
  id: string;
  conversation_id: string;
  author_id: string;
  body: string;
  mentioned_user_ids: string[];
  created_at: string;
}

export interface SupportAdmin {
  id: string;
  first_name: string | null;
  last_name: string | null;
}
